/**
 * InputBase is a base component for all input-based form fields.
 * It handles the common structure (label, wrapper, helper text, etc.)
 */

import { type InputHTMLAttributes } from 'react';
import { useNode, type Node, ROOT_NODE, useEditor } from '@craftjs/core';
import { cx } from '@emotion/css';
import { useRenderContext } from '../../../context/RenderContext';
import { generateUuidV4 } from '../form-utils';
import { getHelpTextStyle } from './fieldStyles';
import { getBreakpointProp } from '../../../utils/breakpointUtils';
import { IconAlert } from './IconAlert';

type InputBaseBaseProps = {
  id: string;
  inputType: string;
  label: string;
  showLabel: boolean;
  placeholder: string;
  required: boolean;
  hidden: boolean;
  helpText: string;
  showHelpText: boolean;
  enforceCharacterLimit: boolean;
  minCharacterLimit: number | null;
  maxCharacterLimit: number | null;
  urlMapping?: string;
};

type InputBaseBreakpointProps = Partial<InputBaseBaseProps>;

export type InputBaseProps = InputBaseBaseProps & {
  tablet?: InputBaseBreakpointProps;
  mobile?: InputBaseBreakpointProps;
};

export const defaultProps: InputBaseProps = {
  id: '',
  inputType: 'text',
  label: 'Text',
  showLabel: false,
  placeholder: 'Name',
  required: false,
  hidden: false,
  helpText: '',
  showHelpText: false,
  enforceCharacterLimit: false,
  minCharacterLimit: null,
  maxCharacterLimit: null,
  urlMapping: '',
};

export const InputBase = (props: Partial<InputBaseProps>) => {
  const fieldProps: InputBaseProps = { ...defaultProps, ...props };
  const { isEditable } = useRenderContext();

  const {
    id: craftNodeId,
    isLocked,
    connectors: { connect, drag },
  } = useNode((node: Node) => ({
    parentId: node.data.parent as string | null,
    isLocked: node.data.custom?.isLocked,
    displayName: node.data.displayName,
  }));

  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // id is not breakpoint-aware, read it directly from props
  let id = fieldProps.id;
  if (!id) {
    const nodeId = generateUuidV4();
    editorActions.history.ignore().setProp(craftNodeId, (props: InputBaseProps) => {
      props.id = nodeId;
    });
    id = nodeId;
    fieldProps.id = nodeId;
  }

  // Read other props using breakpoint-aware getter
  const inputType = getBreakpointProp(fieldProps, 'inputType', breakpoint);
  const label = getBreakpointProp(fieldProps, 'label', breakpoint);
  const showLabel = getBreakpointProp(fieldProps, 'showLabel', breakpoint);
  const placeholder = getBreakpointProp(fieldProps, 'placeholder', breakpoint);
  const required = getBreakpointProp(fieldProps, 'required', breakpoint);
  const hidden = getBreakpointProp(fieldProps, 'hidden', breakpoint);
  const helpText = getBreakpointProp(fieldProps, 'helpText', breakpoint);
  const showHelpText = getBreakpointProp(fieldProps, 'showHelpText', breakpoint);
  const enforceCharacterLimit = getBreakpointProp(fieldProps, 'enforceCharacterLimit', breakpoint);
  const minCharacterLimit = getBreakpointProp(fieldProps, 'minCharacterLimit', breakpoint);
  const maxCharacterLimit = getBreakpointProp(fieldProps, 'maxCharacterLimit', breakpoint);
  const urlMapping = getBreakpointProp(fieldProps, 'urlMapping', breakpoint);

  const disablePointerEvents = isEditable && isLocked;
  const inputTabIndex = isEditable ? (isLocked ? -1 : 0) : 0;

  const inputProps = buildInputProps(
    {
      id,
      inputType,
      label,
      showLabel,
      placeholder,
      required,
      hidden,
      helpText,
      showHelpText,
      enforceCharacterLimit,
      minCharacterLimit,
      maxCharacterLimit,
      urlMapping,
    },
    'field-input',
    disablePointerEvents,
    inputTabIndex,
    `${id}-helper-text`,
  );

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cx(
        'field-node',
        isEditable && 'node-dashed-indicator styled-node',
        'boundary-indicator',
        disablePointerEvents && 'pointer-events-none',
        hidden && 'field-node--hidden',
      )}
      data-field-id={id}
      aria-hidden={hidden || undefined}
    >
      {showLabel && (
        <label className="field-label" htmlFor={id}>
          {label}
          {required ? (
            <span aria-hidden="true" className="field-required-indicator">
              *
            </span>
          ) : null}
        </label>
      )}
      <div className="field-input-wrapper">
        <input {...inputProps} />
        <span className="field-input-icon" aria-hidden="true">
          <IconAlert />
        </span>
      </div>
      <div
        className={cx(
          'field-helper-text-wrapper',
          getHelpTextStyle({
            showHelpText,
            helpText,
          } as InputBaseProps),
        )}
      >
        <p id={`${id}-helper-text`} className="field-helper-text">
          {showHelpText && helpText && helpText.trim().length > 0 ? helpText : '\u200B'}
        </p>
      </div>
    </div>
  );
};

/**
 * Builds input attributes for the input element.
 * @param props - The input base props.
 * @param className - The class name for the input element.
 * @param disablePointerEvents - Whether to disable pointer events.
 * @param tabIndex - The tab index for the input element.
 * @param describedById - The id of the described by element.
 * @returns The input attributes.
 */
function buildInputProps(
  props: InputBaseProps,
  className: string,
  disablePointerEvents: boolean,
  tabIndex: number,
  describedById?: string,
): InputHTMLAttributes<HTMLInputElement> {
  const maxLimit = props.maxCharacterLimit ?? undefined;
  const minLimit = props.minCharacterLimit ?? undefined;

  const attributes: InputHTMLAttributes<HTMLInputElement> = {
    id: props.id,
    name: props.id,
    type: props.inputType,
    className,
    placeholder: props.placeholder,
    required: props.required,
    readOnly: disablePointerEvents,
    tabIndex,
    'aria-required': props.required,
    'aria-describedby': describedById,
    maxLength: props.enforceCharacterLimit ? maxLimit : undefined,
    minLength: props.enforceCharacterLimit ? minLimit : undefined,
    autoComplete: 'new-password',
  };

  (attributes as Record<string, unknown>)['data-character-limit'] = props.enforceCharacterLimit
    ? (maxLimit ?? undefined)
    : undefined;
  (attributes as Record<string, unknown>)['data-min-character-limit'] = props.enforceCharacterLimit
    ? (minLimit ?? undefined)
    : undefined;
  (attributes as Record<string, unknown>)['data-required'] = props.required || undefined;
  (attributes as Record<string, unknown>)['data-url-mapping'] = props.urlMapping || undefined;

  return attributes;
}

// Autofill notes (from Crucible implementation):
// The editable settings for the 'id' and 'autoComplete' attributes are a way
// to ensure browser autofill will be disabled on all major browsers and for
// 1Password users. Chromium does not honor autocomplete="off" but using
// autocomplete="new-password" forces all modern browsers including Chrome to
// disable autofill. Normally 1Pass can only be disabled using
// autocomplete="off", but the word "search" at the beginning or end of an id
// or name will also disable their autofill.
// References:
// - https://1password.community/discussion/comment/606453/#Comment_606453
// - https://codepen.io/ls-andrew-borstein/pen/VwWjoEM
// Current choice: keep semantic values here. If password managers start
// interfering in the Builder again, consider switching to
// "new-password" and/or adjusting the generated id strategy.
